import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import usePermissions from '../hooks/usePermissions';

export default function VotingResultsPage() {
  const navigate = useNavigate();
  const { hasPermission, loading: permissionsLoading } = usePermissions();
  const [results, setResults] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Verifica permessi
    if (!permissionsLoading && !hasPermission('LETTURA_ESITO_VOTAZIONE')) {
      toast.error('Non hai i permessi per visualizzare gli esiti delle votazioni');
      navigate('/dashboard');
      return;
    }
    
    if (!permissionsLoading && hasPermission('LETTURA_ESITO_VOTAZIONE')) {
      fetchResults();
    }
  }, [permissionsLoading, hasPermission, navigate]);

  const fetchResults = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/voting/results', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setResults(response.data);
      
      // Estrai lista candidati unici
      const uniqueCandidates = [];
      const candidateMap = new Map();
      
      response.data.forEach(result => {
        if (!candidateMap.has(result.candidate_id)) {
          candidateMap.set(result.candidate_id, {
            id: result.candidate_id,
            name: result.candidate_name,
            date: result.voting_start_date || 'N/A'
          });
          uniqueCandidates.push({
            id: result.candidate_id,
            name: result.candidate_name,
            date: result.voting_start_date || 'N/A'
          });
        }
      });
      
      setCandidates(uniqueCandidates);
    } catch (error) {
      toast.error('Errore nel caricamento dei risultati');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const filteredResults = selectedCandidate === 'all' 
    ? results 
    : results.filter(r => r.candidate_id === selectedCandidate);

  const getStats = (candidateResults) => {
    const yes = candidateResults.filter(r => r.vote === 'yes').length;
    const no = candidateResults.filter(r => r.vote === 'no').length;
    const total = candidateResults.length;
    return { yes, no, total };
  };

  const selectedCandidateData = candidates.find(c => c.id === selectedCandidate);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="container mx-auto px-4 pt-24 pb-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Esiti Votazioni</h1>
          <p className="text-gray-600">Visualizza i risultati e i feedback delle votazioni</p>
        </div>

        {loading ? (
          <Card>
            <CardContent className="py-8">
              <p className="text-center text-gray-500">Caricamento risultati...</p>
            </CardContent>
          </Card>
        ) : candidates.length === 0 ? (
          <Card>
            <CardContent className="py-8">
              <p className="text-center text-gray-600">Nessun risultato disponibile</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Filtro Candidato */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Filtra per Candidato</CardTitle>
                <CardDescription>Seleziona un candidato per visualizzare i voti ricevuti</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="max-w-md">
                  <Label htmlFor="candidate-filter" className="mb-2 block">Candidato</Label>
                  <Select value={selectedCandidate} onValueChange={setSelectedCandidate}>
                    <SelectTrigger id="candidate-filter">
                      <SelectValue placeholder="Seleziona candidato" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutti i candidati</SelectItem>
                      {candidates.map(candidate => (
                        <SelectItem key={candidate.id} value={candidate.id}>
                          {candidate.name} {candidate.date !== 'N/A' && `(${new Date(candidate.date).toLocaleDateString('it-IT')})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Statistiche */}
            {selectedCandidate !== 'all' && selectedCandidateData && (
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle>{selectedCandidateData.name}</CardTitle>
                  <CardDescription>
                    Data apertura candidatura: {selectedCandidateData.date !== 'N/A' 
                      ? new Date(selectedCandidateData.date).toLocaleDateString('it-IT', { 
                          day: '2-digit', 
                          month: 'long', 
                          year: 'numeric' 
                        })
                      : 'Non disponibile'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <p className="text-3xl font-bold text-gray-900">
                        {getStats(filteredResults).total}
                      </p>
                      <p className="text-sm text-gray-600 mt-1">Totale Voti</p>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <p className="text-3xl font-bold text-green-600">
                        {getStats(filteredResults).yes}
                      </p>
                      <p className="text-sm text-gray-600 mt-1">Voti Favorevoli</p>
                    </div>
                    <div className="text-center p-4 bg-red-50 rounded-lg">
                      <p className="text-3xl font-bold text-red-600">
                        {getStats(filteredResults).no}
                      </p>
                      <p className="text-sm text-gray-600 mt-1">Voti Contrari</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Tabella Voti */}
            <Card>
              <CardHeader>
                <CardTitle>
                  {selectedCandidate === 'all' 
                    ? 'Tutti i Voti' 
                    : `Voti per ${selectedCandidateData?.name}`}
                </CardTitle>
                <CardDescription>
                  {filteredResults.length} {filteredResults.length === 1 ? 'voto' : 'voti'} 
                  {selectedCandidate !== 'all' && ` ricevuti`}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-semibold text-gray-900">Data</th>
                        {selectedCandidate === 'all' && (
                          <th className="text-left py-3 px-4 font-semibold text-gray-900">Candidato</th>
                        )}
                        <th className="text-left py-3 px-4 font-semibold text-gray-900">Votante</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900">Voto</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900">Feedback</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredResults.length === 0 ? (
                        <tr>
                          <td colSpan={selectedCandidate === 'all' ? 5 : 4} className="text-center py-8 text-gray-600">
                            Nessun voto disponibile
                          </td>
                        </tr>
                      ) : (
                        filteredResults.map((result, index) => (
                          <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-3 px-4 text-gray-600">
                              {result.date_created 
                                ? new Date(result.date_created).toLocaleDateString('it-IT', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })
                                : 'N/A'}
                            </td>
                            {selectedCandidate === 'all' && (
                              <td className="py-3 px-4 font-medium text-gray-900">
                                {result.candidate_name}
                              </td>
                            )}
                            <td className="py-3 px-4 text-gray-900">
                              {result.voter_name}
                            </td>
                            <td className="py-3 px-4">
                              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                                result.vote === 'yes' 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {result.vote === 'yes' ? '👍 SÌ' : '👎 NO'}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-gray-700">
                              {result.feedback}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
